import { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, onSnapshot, collection, addDoc, getDocs, deleteDoc } from 'firebase/firestore';

// Main App component
export default function App() {
  const [db, setDb] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // States for UI and app logic
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'create', 'join', 'room'
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [members, setMembers] = useState([]);
  const [room, setRoom] = useState(null);
  const [userName, setUserName] = useState('');
  const roomCodeRef = useRef(null);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);

  // Firebase configuration.
  const firebaseConfig = {
    apiKey: "AIzaSyD2i-O5sKHam7xWWLPNRAfE1o3HThfC5I0",
    authDomain: "file-drop-87c3a.firebaseapp.com",
    projectId: "file-drop-87c3a",
    storageBucket: "file-drop-87c3a.firebasestorage.app",
    messagingSenderId: "670093871299",
    appId: "1:670093871299:web:2c6ae1154508852f067a76"
  };

  const APP_ID = 'file-sharing-app-local'; // A unique app ID for local use.

  // Function to initialize Firebase
  useEffect(() => {
    try {
      const app = initializeApp(firebaseConfig);
      const auth = getAuth(app);
      setDb(getFirestore(app));

      // Sign in anonymously to get a user ID
      signInAnonymously(auth).then((userCredential) => {
        setUserId(userCredential.user.uid);
        setIsAuthReady(true);
      }).catch((e) => {
        console.error("Anonymous Sign-in Error:", e);
        setIsAuthReady(true);
      });
      
    } catch (e) {
      console.error("Firebase Initialization Error:", e);
      setIsAuthReady(true);
    }
  }, []);

  // Effect to handle URL parameters for direct joining
  useEffect(() => {
    if (!isAuthReady) return;

    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('roomCode');

    if (codeFromUrl) {
      setRoomCode(codeFromUrl);
      setCurrentPage('join');
      showMessage(`Room code pre-filled from link.`);
    }
  }, [isAuthReady]);

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(null), 3000);
  };

  const generateRoomCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const createRoom = async () => {
    if (!db || !userId) return;
    setIsLoading(true);
    setError('');

    const displayName = userName.trim() || `User_${userId.substring(0, 6)}`;

    try {
      const newRoomCode = generateRoomCode();
      const roomRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', newRoomCode);
      const roomDoc = await getDoc(roomRef);

      if (roomDoc.exists()) {
        throw new Error("Room with this code already exists. Please try again.");
      }

      await setDoc(roomRef, {
        hostId: userId,
        members: [{ id: userId, name: displayName }],
        createdAt: new Date(),
      });

      setRoomCode(newRoomCode);
      setCurrentPage('room');
      showMessage(`Room created with code: ${newRoomCode}`);
    } catch (e) {
      console.error("Error creating room:", e);
      setError("Failed to create room. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const joinRoom = async () => {
    if (!db || !userId || roomCode.length !== 6) {
      setError("Please enter a valid 6-digit room code.");
      return;
    }
    setIsLoading(true);
    setError('');

    const displayName = userName.trim() || `User_${userId.substring(0, 6)}`;

    try {
      const roomRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode);
      const roomDoc = await getDoc(roomRef);

      if (!roomDoc.exists()) {
        throw new Error("Room does not exist. Please check the code.");
      }

      const roomData = roomDoc.data();
      const existingMember = roomData.members.find(member => member.id === userId);

      if (!existingMember) {
        const newMember = { id: userId, name: displayName };
        const updatedMembers = [...roomData.members, newMember];
        await setDoc(roomRef, { members: updatedMembers }, { merge: true });
      }

      setMembers(roomData.members);
      setCurrentPage('room');
      showMessage(`Joined room: ${roomCode}`);
    } catch (e) {
      console.error("Error joining room:", e);
      setError("Failed to join room. " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelectOrDrop = async (file) => {
    if (!db || !userId || !roomCode || !file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64String = e.target.result;
        const filesCollectionRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode, 'files');
        await addDoc(filesCollectionRef, {
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64String,
          senderId: userId,
          timestamp: new Date(),
        });
        showMessage(`File '${file.name}' shared!`);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error("Error uploading file:", e);
      setError("Failed to share file. Please try again.");
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setError('');
    const droppedFile = event.dataTransfer.files[0];
    handleFileSelectOrDrop(droppedFile);
  };

  const handleFileInputChange = (event) => {
    setError('');
    const selectedFile = event.target.files[0];
    handleFileSelectOrDrop(selectedFile);
  };

  // The main effect to listen for real-time changes
  useEffect(() => {
    if (currentPage !== 'room' || !db || !userId || !roomCode || !isAuthReady) {
      return;
    }

    const roomRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode);
    const unsubscribeRoom = onSnapshot(roomRef, (docSnap) => {
      if (docSnap.exists()) {
        setRoom(docSnap.data());
        setMembers(docSnap.data().members || []);
      } else {
        setCurrentPage('home');
        setError('The room has been closed by the host.');
        setFiles([]);
        setMembers([]);
        setRoomCode('');
      }
    });

    const filesCollectionRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode, 'files');
    const unsubscribeFiles = onSnapshot(filesCollectionRef, (querySnapshot) => {
      const fetchedFiles = [];
      querySnapshot.forEach((doc) => {
        fetchedFiles.push({ id: doc.id, ...doc.data() });
      });
      fetchedFiles.sort((a, b) => b.timestamp - a.timestamp);
      setFiles(fetchedFiles);
    });

    return () => {
      unsubscribeRoom();
      unsubscribeFiles();
    };
  }, [db, userId, roomCode, isAuthReady, currentPage]);

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.data;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseRoom = async () => {
    if (!db || !userId || !roomCode) return;
    setIsLoading(true);

    try {
      const roomRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode);
      const roomDoc = await getDoc(roomRef);

      if (roomDoc.exists() && roomDoc.data().hostId === userId) {
        const filesCollectionRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode, 'files');
        const fileDocs = await getDocs(filesCollectionRef);
        fileDocs.forEach(async (d) => {
          await deleteDoc(doc(filesCollectionRef, d.id));
        });
        await deleteDoc(roomRef);
        setCurrentPage('home');
        setRoomCode('');
        setFiles([]);
        setMembers([]);
        showMessage('Room closed.');
      } else {
        setError('You are not the host and cannot close this room.');
      }
    } catch (e) {
      console.error("Error closing room:", e);
      setError("Failed to close room.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFiles = async () => {
    if (!db || !roomCode) return;
    setIsLoading(true);
    try {
      const filesCollectionRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'rooms', roomCode, 'files');
      const fileDocs = await getDocs(filesCollectionRef);
      fileDocs.forEach(async (d) => {
        await deleteDoc(doc(filesCollectionRef, d.id));
      });
      showMessage('All files cleared.');
    } catch (e) {
      console.error("Error clearing files:", e);
      setError("Failed to clear files.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
            showMessage('Copied!');
        }).catch(err => {
            console.error('Could not copy text: ', err);
            showMessage('Failed to copy.');
        });
    } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            showMessage('Copied!');
        } catch (err) {
            console.error('Failed to copy text: ', err);
            showMessage('Failed to copy.');
        }
        document.body.removeChild(textArea);
    }
  };

  const renderFilePreview = (file) => {
    const isImage = file.type.startsWith('image/');
    const isText = file.type.startsWith('text/');
    const isPDF = file.type === 'application/pdf';

    if (isImage) {
      return <img src={file.data} alt={file.name} className="file-preview-image" />;
    }
    if (isText) {
      // For simplicity, render first 100 characters of text file
      const textContent = atob(file.data.split(',')[1]);
      return <div className="file-preview-text-container"><pre className="file-preview-text">{textContent.substring(0, 100)}...</pre></div>;
    }
    if (isPDF) {
      return <div className="file-preview-icon">📄</div>;
    }
    return <div className="file-preview-icon">🗂️</div>;
  };

  const renderPage = () => {
    const shareableLink = window.location.origin + '?roomCode=' + roomCode;
    switch (currentPage) {
      case 'home':
        return (
          <div className="card">
            <h1>ShareDrop</h1>
            <p className="subtitle">
              Create a room or join with a code to start sharing files instantly.
            </p>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name (optional)"
              className="input-field mb-4"
              maxLength="20"
            />
            <div className="button-group">
              <button onClick={createRoom} className="btn primary">
                Create Room
              </button>
              <button onClick={() => setCurrentPage('join')} className="btn secondary">
                Join Room
              </button>
            </div>
          </div>
        );

      case 'join':
        return (
          <div className="card">
            <h2>Join a Room</h2>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name (optional)"
              className="input-field mb-4"
              maxLength="20"
            />
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength="6"
              className="input-field"
            />
            {error && <p className="error-message">{error}</p>}
            <button
              onClick={joinRoom}
              disabled={roomCode.length !== 6 || isLoading}
              className={`btn primary mt-4 ${roomCode.length !== 6 || isLoading ? 'disabled' : ''}`}
            >
              {isLoading ? 'Joining...' : 'Join'}
            </button>
            <button
              onClick={() => { setCurrentPage('home'); setRoomCode(''); setError(''); }}
              className="btn text-btn"
            >
              Back to Home
            </button>
          </div>
        );

      case 'room':
        const isHost = room && room.hostId === userId;
        return (
          <div className="room-container">
            <h2 className="room-title">Room Code: <span ref={roomCodeRef} className="room-code">{roomCode}</span></h2>
            <p className="room-subtitle">Share this code with others to collaborate!</p>
            <div className="share-link-container">
                <input
                    type="text"
                    value={shareableLink}
                    className="share-link-input"
                    readOnly
                />
                <button onClick={() => handleCopyToClipboard(shareableLink)} className="btn copy-btn">
                    Copy Link
                </button>
            </div>
            
            <div
              className="drop-area"
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              <svg className="drop-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <p className="drop-text">Drag and drop your files here</p>
              <p className="drop-subtext">or click to browse</p>
            </div>

            {error && <p className="error-message">{error}</p>}

            <div className="members-section">
              <h3 className="section-title">Room Members ({members.length})</h3>
              <div className="members-list">
                {members.map((member) => (
                  <span key={member.id} className="member-tag">
                    {member.name}
                  </span>
                ))}
              </div>
            </div>

            {files.length > 0 && (
              <div className="files-section">
                <div className="flex-header">
                  <h3 className="section-title">Shared Files</h3>
                  <button onClick={handleClearFiles} className="btn small-btn">
                    Clear All
                  </button>
                </div>
                <div className="files-grid">
                  {files.map((file) => (
                    <div key={file.id} className="file-item">
                      <div className="file-preview-container">
                        {renderFilePreview(file)}
                      </div>
                      <div className="file-info">
                        <p className="file-name">{file.name}</p>
                        <p className="file-size">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => handleDownload(file)}
                        className="btn download-btn"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="room-actions">
              <button
                onClick={() => { setCurrentPage('home'); setRoomCode(''); setError(''); }}
                className="btn secondary"
              >
                Back to Home
              </button>
              {isHost && (
                <button
                  onClick={handleCloseRoom}
                  disabled={isLoading}
                  className={`btn danger ${isLoading ? 'disabled' : ''}`}
                >
                  {isLoading ? 'Closing...' : 'Close Room'}
                </button>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      <style>{`
        /* App.css */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');

        :root {
          --primary-color: #3b82f6;
          --primary-hover: #2563eb;
          --secondary-color: #4b5563;
          --secondary-hover: #374151;
          --danger-color: #ef4444;
          --danger-hover: #dc2626;
          --background-start: #1f2937;
          --background-end: #000000;
          --card-bg: rgba(31, 41, 55, 0.7);
          --text-color: #d1d5db;
          --title-color: #ffffff;
          --accent-color: #60a5fa;
          --border-color: #4b5563;
        }

        body {
          font-family: 'Inter', sans-serif;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 1rem;
          background: linear-gradient(135deg, var(--background-start), var(--background-end));
          position: relative;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 100;
        }

        .spinner {
          width: 4rem;
          height: 4rem;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-top-color: var(--primary-color);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .card {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2.5rem;
          background: var(--card-bg);
          border-radius: 1.5rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(8px);
          width: 100%;
          max-width: 24rem;
          text-align: center;
        }

        .card h1 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--title-color);
          margin-bottom: 1rem;
        }

        .card h2 {
          font-size: 2rem;
          font-weight: 700;
          color: var(--title-color);
          margin-bottom: 1.5rem;
        }

        .subtitle {
          font-size: 0.875rem;
          color: var(--text-color);
          margin-bottom: 2rem;
          line-height: 1.5;
        }

        .button-group {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          width: 100%;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0.75rem;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          color: var(--title-color);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .btn.primary {
          background-color: var(--primary-color);
        }

        .btn.primary:hover {
          background-color: var(--primary-hover);
          transform: scale(1.05);
        }

        .btn.secondary {
          background-color: var(--secondary-color);
        }

        .btn.secondary:hover {
          background-color: var(--secondary-hover);
          transform: scale(1.05);
        }

        .btn.danger {
          background-color: var(--danger-color);
        }

        .btn.danger:hover {
          background-color: var(--danger-hover);
        }

        .btn.text-btn {
          background: none;
          font-size: 1rem;
          color: var(--primary-color);
          padding: 0.5rem;
          margin-top: 0.5rem;
        }

        .btn.text-btn:hover {
          color: var(--primary-hover);
        }

        .btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none;
        }

        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          text-align: center;
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--title-color);
          background-color: var(--secondary-color);
          border: 2px solid transparent;
          border-radius: 0.75rem;
          transition: border-color 0.3s;
          outline: none;
          margin-bottom: 1.5rem;
        }

        .input-field:focus {
          border-color: var(--primary-color);
        }

        .error-message {
          color: var(--danger-color);
          font-size: 0.875rem;
          margin-top: 0.5rem;
        }

        .room-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2.5rem;
          background: var(--card-bg);
          border-radius: 1.5rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(8px);
          width: 100%;
          max-width: 48rem;
          text-align: center;
        }

        .room-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--title-color);
          margin-bottom: 0.5rem;
        }

        .room-code {
          font-family: 'Courier New', Courier, monospace;
          color: var(--accent-color);
          font-size: 1.5rem;
        }

        .room-subtitle {
          font-size: 0.75rem;
          color: var(--text-color);
          margin-bottom: 1.5rem;
        }

        .copy-btn {
            background-color: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            margin-bottom: 1.5rem;
            transition: background-color 0.3s;
        }

        .copy-btn:hover {
            background-color: #1e40af;
        }
        
        .drop-area {
          width: 100%;
          height: 12rem;
          border: 4px dashed var(--border-color);
          border-radius: 1rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .drop-area:hover {
          border-color: var(--primary-color);
          background-color: rgba(0, 0, 0, 0.1);
        }

        .drop-icon {
          width: 3rem;
          height: 3rem;
          color: var(--border-color);
          margin-bottom: 0.5rem;
        }

        .drop-text {
          color: var(--text-color);
          font-weight: 600;
        }

        .drop-subtext {
          font-size: 0.75rem;
          color: var(--text-color);
          margin-top: 0.25rem;
        }

        .members-section, .files-section {
          width: 100%;
          margin-top: 2.5rem;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--title-color);
          margin-bottom: 0.75rem;
          text-align: center;
        }

        .members-list {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
        }

        .member-tag {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
          background-color: var(--secondary-color);
          color: var(--text-color);
          border-radius: 9999px;
        }

        .files-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .file-item {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 1rem;
          background-color: var(--secondary-color);
          border-radius: 0.75rem;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .file-info {
          flex-grow: 1;
        }

        .file-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--title-color);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-size {
          font-size: 0.75rem;
          color: var(--text-color);
          margin-top: 0.25rem;
        }

        .download-btn {
          margin-top: 1rem;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          border-radius: 0.5rem;
          background-color: var(--primary-color);
          transition: background-color 0.3s ease;
          box-shadow: none;
        }

        .download-btn:hover {
          background-color: var(--primary-hover);
        }

        .room-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
        }
        
        .flex-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
        }

        .small-btn {
            font-size: 0.75rem;
            padding: 0.5rem 0.75rem;
            width: auto;
            background-color: #4b5563;
        }

        .small-btn:hover {
            background-color: #374151;
        }

        .snackbar {
            position: fixed;
            bottom: 2rem;
            left: 50%;
            transform: translateX(-50%);
            background-color: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 0.5rem;
            z-index: 100;
            animation: fadeInOut 3s forwards;
            font-size: 0.875rem;
        }

        @keyframes fadeInOut {
            0% { opacity: 0; transform: translateX(-50%) translateY(20px); }
            10% { opacity: 1; transform: translateX(-50%) translateY(0); }
            90% { opacity: 1; transform: translateX(-50%) translateY(0); }
            100% { opacity: 0; transform: translateX(-50%) translateY(20px); }
        }

        .file-preview-container {
            height: 8rem;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 0.5rem;
            background-color: rgba(0,0,0,0.2);
            border-radius: 0.5rem;
            overflow: hidden;
        }

        .file-preview-image {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }

        .file-preview-icon {
            font-size: 4rem;
        }

        .file-preview-text-container {
            padding: 0.5rem;
            height: 100%;
            overflow: hidden;
        }

        .file-preview-text {
            white-space: pre-wrap;
            word-wrap: break-word;
            font-size: 0.75rem;
            color: white;
            text-align: left;
        }
        .share-link-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            margin-bottom: 1.5rem;
        }
        .share-link-input {
            width: 100%;
            padding: 0.75rem;
            text-align: center;
            font-size: 0.875rem;
            color: var(--title-color);
            background-color: var(--secondary-color);
            border: 2px solid transparent;
            border-radius: 0.75rem;
            outline: none;
            margin-bottom: 0.5rem;
        }
        .share-link-input:hover {
            cursor: pointer;
        }
      `}</style>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      {message && <div className="snackbar">{message}</div>}
      {renderPage()}
    </div>
  );
}